import { useEffect, useMemo, useRef, useState } from 'react';
import './CallChat2.css';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { callChat2I18n } from './i18n';

function groupMessages(messages) {
  const groups = [];
  let currentGroup = null;

  messages.forEach((msg) => {
    if (!currentGroup || currentGroup.speaker !== msg.speaker) {
      currentGroup = {
        speaker: msg.speaker,
        messages: [msg],
      };
      groups.push(currentGroup);
    } else {
      currentGroup.messages.push(msg);
    }
  });

  return groups;
}

const normalizeSpeaker = (value) => {
  const speaker = String(value || '').toLowerCase();
  if (speaker === 'agent' || speaker === 'agente') return 'Agente';
  if (speaker === 'customer' || speaker === 'cliente') return 'Cliente';
  return value || '';
};

const parseTranscription = (audioText) => {
  if (!audioText || typeof audioText !== 'string') return [];

  const lines = audioText.split(';').filter((line) => line.trim());

  return lines
    .map((line, index) => {
      const match = line.match(/^(\d+)-(\d+)-([^-]+)-(.+)$/);

      if (match) {
        const [, startTime, endTime, rawSpeaker, text] = match;
        const speaker = normalizeSpeaker(rawSpeaker.trim());

        return {
          id: index,
          startTime: parseInt(startTime, 10),
          endTime: parseInt(endTime, 10),
          speaker,
          text: text.trim(),
        };
      }
      return null;
    })
    .filter(Boolean)
    .sort((a, b) => a.startTime - b.startTime);
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightMatch = (text, query) => {
  if (!query) return text;
  const escaped = escapeRegExp(query);
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, idx) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={idx} style={{ backgroundColor: '#ffe564' }}>
        {part}
      </mark>
    ) : (
      part
    )
  );
};

export default function CallChat2({ callData, searchQuery }) {
  const { t } = useScopedTranslation(callChat2I18n);
  const [maxHeight, setMaxHeight] = useState(null);
  const containerRef = useRef(null);
  const messageRefs = useRef(new Map());

  const [matchIndexes, setMatchIndexes] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  const transcription = callData?.audioText || callData?.transcription || '';
  const messages = useMemo(
    () => parseTranscription(transcription),
    [transcription]
  );
  const grouped = useMemo(() => groupMessages(messages), [messages]);

  // Resize handling
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const top = containerRef.current.getBoundingClientRect().top;
        const availableHeight = window.innerHeight - top - 48;
        setMaxHeight(availableHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Search indexing
  useEffect(() => {
    if (!searchQuery) {
      setMatchIndexes([]);
      setCurrentMatchIndex(0);
      return;
    }
    const matches = Array.from(messageRefs.current.entries())
      .filter(([, el]) =>
        el?.textContent.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .map(([id]) => id);

    setMatchIndexes(matches);
    setCurrentMatchIndex(0);
  }, [searchQuery]);

  useEffect(() => {
    const currentId = matchIndexes[currentMatchIndex];
    if (!currentId) return;
    const el = messageRefs.current.get(currentId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [currentMatchIndex, matchIndexes]);

  useEffect(() => {
    messageRefs.current = new Map();
  }, [messages]);

  const handlePrev = () => {
    setCurrentMatchIndex((prev) =>
      prev === 0 ? matchIndexes.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentMatchIndex((prev) =>
      prev === matchIndexes.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div
      ref={containerRef}
      className='chat-container'
      style={
        maxHeight ? { maxHeight: `${maxHeight}px`, overflowY: 'auto' } : {}
      }
    >
      {matchIndexes.length > 0 && (
        <div className='match-navigator'>
          <span>
            {currentMatchIndex + 1} of {matchIndexes.length}
          </span>
          <button onClick={handlePrev}>↑</button>
          <button onClick={handleNext}>↓</button>
        </div>
      )}

      {messages.length === 0 && (
        <p className='caption text-center font-light'>
          {t('chat_no_transcript')}
        </p>
      )}
      {grouped.map((group, idx) => (
        <div
          key={idx}
          className={`chat-message ${
            group.speaker === 'Agente' ? 'left' : 'right'
          }`}
        >
          <div className='chat-sender'>
            {group.speaker === 'Agente' ? t('chat_consultant') : t('chat_client')}
          </div>
          {group.messages.map((msg) => (
            <div
              key={msg.id}
              ref={(el) => messageRefs.current.set(msg.id, el)}
              className={`chat-bubble ${
                group.speaker === 'Agente' ? 'agent' : 'client'
              }`}
            >
              {highlightMatch(msg.text, searchQuery)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
